import {
  Button,
  Center,
  Divider,
  Flex,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { ReactComponent as BulbIcon } from 'assets/icons/bulb.svg';
import { ReactComponent as GenerateIcon } from 'assets/icons/design_ai.svg';
import { ReactComponent as RightCircleSolidIcon } from 'assets/icons/right_circle_solid.svg';
import { ReactComponent as StopIcon } from 'assets/icons/stop_circle.svg';
import { useScreen } from 'hooks/useScreen/useScreen';
import { ChatCreditsInfo } from 'modules/common/components/Chat/ChatCreditsInfo';
import {
  ChangeEvent,
  KeyboardEvent,
  Suspense,
  forwardRef,
  lazy,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SpreadSheetImportProps } from 'types/spreadsheet';

import { AutoResizeTextarea } from '../AutoResizeTextArea/AutoResizeTextArea';
import { FileTag } from '../FileTag';
import GenericLoader from '../GenericLoader';
import { ChatInputProps, RightElementProps, RightElementRefProps, SubmitButtonProps } from './Chat.types';
import { ChatInputWrapper } from './ChatInputWrapper';

const SpreadSheetImport = lazy(
  () =>
    import(
      /* webpackChunkName: "SpreadSheetImportChat" */
      'modules/common/components/SpreadSheetImport'
    ),
);

const SpreadSheetImportLazy = forwardRef<HTMLInputElement, SpreadSheetImportProps>((props, ref) => {
  return (
    <Suspense fallback={<GenericLoader />}>
      <SpreadSheetImport {...props} ref={ref} />
    </Suspense>
  );
});

SpreadSheetImportLazy.displayName = 'SpreadSheetImportLazy';

const chatInputWrapperTestId = 'chat-input-wrapper';
const chatInputTestId = 'chat-input';

/**
 * Render the chat input in the ChatPanel
 * Trigger the submit event when user press 'Enter' or click the generate button.
 *
 * If the spreadsheet upload function is needed, pass the onUpload callback function.
 *
 * @param containerProps
 * @param onSubmit {function} callback function
 * @param onComplete
 * @param onClearUpload
 * @param isDisabled {boolean} optional
 * @param disableFileUpload
 * @param category
 * @param lwoCategory
 * @param lwoSubCategory
 * @param placeholder
 * @param categoryText
 * @param submitButtonRef
 * @param variant
 * @param isAiToolsMasterPage
 * @param userCredits
 * @param isLoggedInUser
 * @param disableUserPrompt
 * @param currentPromptMessage
 * @param setCurrentPromptMessage
 *  @param isSubmittingPrompt
 * @returns
 */
export const ChatInput = ({
  containerProps,
  onSubmit,
  onUpload: onComplete,
  onClearUpload,
  onStop,
  isDisabled,
  disableFileUpload = false,
  category,
  categoryText,
  submitButtonRef,
  placeholder,
  variant = 'inline',
  isAiToolsMasterPage,
  userCredits,
  isLoggedInUser,
  disableUserPrompt,
  currentPromptMessage,
  setCurrentPromptMessage,
  isSubmittingPrompt,
  lwoCategory,
  lwoSubCategory,
  iconLabel,
  variantInlineInputId,
  suggestions = [],
}: ChatInputProps) => {
  const { t } = useTranslation('common_components', {
    useSuspense: false,
    keyPrefix: 'chat.promptInput',
  });
  const { isMobile } = useScreen();
  const location = useLocation();
  const isBlogPage = location.pathname.includes('/blog');
  const [inputValue, setInputValue] = useState('');
  const [shuffledSuggestions, setShuffledSuggestions] = useState<string[]>([]);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);

  const rightEleRef = useRef<RightElementRefProps>(null);

  // Shuffle suggestions when component mounts or suggestions change
  useEffect(() => {
    if (suggestions.length > 0) {
      const shuffled = [...suggestions].sort(() => Math.random() - 0.5);
      setShuffledSuggestions(shuffled);
      setCurrentSuggestionIndex(0);
    }
  }, [suggestions]);

  // Handle suggestion click
  const handleSuggestionClick = () => {
    if (shuffledSuggestions.length === 0) return;

    // Set the current suggestion as input value
    const suggestion = shuffledSuggestions[currentSuggestionIndex];
    setInputValue(suggestion);

    // Move to next suggestion, cycling through the array
    const nextIndex = (currentSuggestionIndex + 1) % shuffledSuggestions.length;
    setCurrentSuggestionIndex(nextIndex);

    // No reshuffling needed - just cycle through the same shuffled array
  };

  useImperativeHandle(
    submitButtonRef,
    () => {
      if (rightEleRef.current?.submitButton) return rightEleRef.current.submitButton;

      // Should not reach here. This is the type guard
      return document.createElement('button');
    },
    [],
  );

  // Input event handler
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setInputValue(e.target.value);
  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key !== 'Enter' || !inputValue) return;
    if (e.key === 'Enter' && e.shiftKey) return;

    handleSubmit(inputValue);
  };

  // Button event handler
  const handleClick = () => {
    if (!inputValue) return;

    handleSubmit(inputValue);
  };

  // Update the input value when the prompt message is changed
  // Only update once after prompt submission is done
  useEffect(() => {
    if (!isSubmittingPrompt) return;
    if (!!currentPromptMessage && currentPromptMessage !== inputValue) {
      setInputValue(currentPromptMessage);
    }
  }, [currentPromptMessage, inputValue, isSubmittingPrompt]);

  // Trigger callback event
  const handleSubmit = (inputValue: string) => {
    setInputValue(inputValue);

    if (typeof setCurrentPromptMessage === 'function') {
      setCurrentPromptMessage(inputValue);
    }
    onSubmit?.(inputValue);
  };

  const defaultPlaceholder = t('placeholder');
  const placeHolder = placeholder || defaultPlaceholder;
  const inputAriaLabel = `Chat input: ${placeHolder}`;

  return (
    <ChatInputWrapper
      className={`${variant === 'inline' ? 'inline-wrapper' : ''} ${isBlogPage ? 'blogWrapper' : ''}`}
      mt='2px'
      pr='3'
      data-testid={chatInputWrapperTestId}
      {...containerProps}
      flexDirection='column'
    >
      <InputGroup className={`${variant !== 'inline' ? 'chatInputRow' : undefined}`}>
        <Flex className='chatInputWrapperContainer' direction='row' w='100%'>
          {isBlogPage && (
            <InputLeftElement
              color='violet.700'
              fontSize='1.2em'
              top={isBlogPage ? '2px' : '0'}
              children={<GenerateIcon width='24px' height='24px' />}
            />
          )}
          {variant === 'inline' ? (
            <AutoResizeTextarea
              isDisabled={isDisabled}
              boxShadow='sm'
              placeholder={placeHolder}
              value={inputValue}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              aria-label={inputAriaLabel}
              data-testid={chatInputTestId}
              paddingLeft='40px'
              paddingRight='84px'
              resize='none'
              border='none'
              id={variantInlineInputId}
              sx={{
                '&::placeholder': {
                  fontSize: { base: 'sm', md: 'md' },
                },
              }}
            />
          ) : (
            <Textarea
              className='chatInputField'
              isDisabled={isDisabled || disableUserPrompt}
              borderWidth={0}
              borderRadius={0}
              pl={isBlogPage ? '44px' : 3}
              pt={isBlogPage ? '12px' : '8px'}
              h='100%'
              borderTopRightRadius='8px'
              borderTopLeftRadius='8px'
              placeholder={placeHolder}
              value={inputValue}
              onChange={handleChange}
              _focusVisible={{ borderColor: 'none', boxShadow: 'none' }}
              onKeyPress={handleKeyPress}
              aria-label={inputAriaLabel}
              data-testid={chatInputTestId}
            />
          )}
          <InputRightElement
            width={'fit-content'}
            minW={'fit-content'}
            maxW={'max-content'}
            paddingX={2}
            top={{ md: !isLoggedInUser && variant === 'standaloneSubmit' ? 'unset' : 0 }}
            left={{ base: !isLoggedInUser && variant === 'standaloneSubmit' ? 0 : 'unset', md: 'unset' }}
            bottom={{ base: variant === 'standaloneSubmit' ? 0 : 'unset' }}
            display={{ base: !isLoggedInUser && variant === 'standaloneSubmit' && userCredits === 0 ? 'none' : 'flex' }}
          >
            <RightElement
              ref={rightEleRef}
              isDisabled={isDisabled}
              onSubmit={variant !== 'standaloneSubmit' ? handleClick : undefined}
              onComplete={onComplete}
              onClearUpload={onClearUpload}
              disableFileUpload={disableFileUpload}
              iconLabel={iconLabel}
            />
          </InputRightElement>
        </Flex>
      </InputGroup>
      {suggestions.length > 0 && (
        <Flex position='absolute' top='0' right='0' p={4}>
          <Tooltip
            label='Shuffle prompts for inspiration'
            placement={isMobile ? 'top' : 'right'}
            borderRadius='base'
            p={2}
            w='148px'
            hasArrow
          >
            <IconButton
              variant='ghost'
              aria-label='Suggestions'
              icon={<BulbIcon />}
              isDisabled={isDisabled}
              onClick={handleSuggestionClick}
            />
          </Tooltip>
        </Flex>
      )}
      {variant === 'standaloneSubmit' && (
        <Flex
          className='chatSubmitRow'
          direction='row'
          w='full'
          alignItems='center'
          justifyContent='flex-end'
          mt={2}
          mb={3}
          px={3}
        >
          <Flex className='chatSubmitBtn' alignSelf='flex-end' gap={4} flexDir={{ base: 'column', md: 'row' }}>
            {!isLoggedInUser && variant === 'standaloneSubmit' && (
              <ChatCreditsInfo
                credits={userCredits}
                category={category}
                lwoCategory={lwoCategory}
                lwoSubCategory={lwoSubCategory}
              />
            )}
            <SubmitButton
              submitButtonRef={submitButtonRef}
              isDisabled={!!(isDisabled || disableUserPrompt)}
              onClick={handleClick}
              onStop={onStop}
            />
          </Flex>
        </Flex>
      )}
    </ChatInputWrapper>
  );
};

const ACTIVE_STYLE = {
  bgGradient: 'linear(to-r, violet.600, violet.600)',
  outlineWidth: '2px',
  outlineOffset: '-2px',
  outlineColor: 'violet.800',
};
const HOVER_STYLE = {
  bgGradient: 'linear(to-r, violet.600, violet.600)',
};

/**
 * It shows stop button when it has onStop function, if it is null. it only disabled
 * @param submitButtonRef
 * @param isDisabled
 * @param onClick
 * @param onStop
 * @returns
 */
const SubmitButton = ({ submitButtonRef, isDisabled, onClick, onStop }: SubmitButtonProps) => {
  const { t } = useTranslation('common_components', { useSuspense: false, keyPrefix: 'chat.promptInput' });
  const [isStopDisabled, setIsStopDisabled] = useState(false);

  const enableStopButton = !!onStop;

  // Reset stop disabled state when isDisabled prop changes
  useEffect(() => {
    setIsStopDisabled(false);
  }, [isDisabled]);

  const handleStop = () => {
    setIsStopDisabled(true);
    onStop?.();
  };

  return (
    <>
      {enableStopButton ? (
        <Button
          variant={'outline'}
          borderRadius='base'
          onClick={handleStop}
          isDisabled={isStopDisabled}
          aria-label={t('generateButton')}
          data-testid='chat-input-stop-button'
          leftIcon={<StopIcon width='24px' height='24px' />}
          borderColor='gray.200'
          _hover={{
            bg: 'hover.gray',
          }}
          id='chat-input-button'
        >
          {t('stopButton')}
        </Button>
      ) : (
        <Button
          ref={submitButtonRef}
          isDisabled={isDisabled}
          bgGradient='linear(to-r, #9747FF, #0073E6)'
          outlineOffset='0'
          _hover={HOVER_STYLE}
          _active={ACTIVE_STYLE}
          color='white'
          borderRadius='md'
          maxWidth='fit-content'
          minWidth='fit-content'
          width='fit-content'
          onClick={onClick}
          aria-label={t('generateButton')}
          data-testid='chat-input-button'
          leftIcon={<GenerateIcon width='24px' height='24px' />}
          id='chat-input-button'
        >
          {t('generateButton')}
        </Button>
      )}
    </>
  );
};

const chatInputButtonTestId = 'chat-input-button';

const ICON_STYLE = {
  'svg path': {
    fill: 'var(--vg-colors-violet-500)',
  },
  '&:hover svg path': {
    opacity: 0.8,
  },
};
export const RightElement = forwardRef<RightElementRefProps, RightElementProps>(
  ({ isDisabled, disableFileUpload = false, onSubmit, onClearUpload, onComplete: onCompleteProp, iconLabel }, ref) => {
    const { t } = useTranslation('common_components', { useSuspense: false, keyPrefix: 'chat.promptInput' });
    const [fileName, setFileName] = useState('');
    const { isMobile } = useScreen();

    const showSpreadsheetImport: boolean = !disableFileUpload && !fileName && !!onCompleteProp; // no uploads and have the upload handler
    const showFileTag = !!fileName && !!onCompleteProp; // have uploads and have the upload handler
    const showDivider = !!onSubmit && (showSpreadsheetImport || showFileTag); // have spreadsheet feature and the submit button.

    const submitRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const onComplete = useCallback<SpreadSheetImportProps['onComplete']>(
      (tableData, fileType, fileName) => {
        setFileName(fileName);
        onCompleteProp?.(tableData, fileType, fileName);
      },
      [onCompleteProp],
    );

    useImperativeHandle(ref, () => ({
      submitButton: submitRef.current,
      inputElement: inputRef.current,
      submitRef,
      inputRef,
    }));

    const clearUpload = useCallback(() => {
      setFileName('');
      onClearUpload?.();

      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.files = null;
      }
    }, [onClearUpload]);

    return (
      <>
        {showSpreadsheetImport && (
          <SpreadSheetImportLazy
            ref={inputRef}
            isDisabled={isDisabled}
            icon
            onComplete={onComplete}
            iconLabel={iconLabel}
            hasSuccessToast={false}
          />
        )}
        {showFileTag && <FileTag name={isMobile ? '' : fileName} onDelete={clearUpload} />}
        {showDivider && <Divider orientation='vertical' marginX={1} height={'45%'} />}
        {!!onSubmit && (
          <Button
            ref={submitRef}
            isDisabled={isDisabled}
            variant='ghost'
            p='0'
            m='0'
            w='6'
            h='6'
            minW='6'
            borderRadius='xl'
            onClick={onSubmit}
            aria-label={t('generateButton')}
            _hover={{}} // Reset hover style
            data-testid={chatInputButtonTestId}
          >
            <Center width='5' height='5' sx={ICON_STYLE}>
              <RightCircleSolidIcon width={'100%'} height={'100%'} />
            </Center>
          </Button>
        )}
      </>
    );
  },
);

RightElement.displayName = 'RightElement';
